import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { getBoardRouteKey } from "helpers";
import { createLogger } from "logging";
import { BoardPermissionModel } from "models/BoardPermissionModel";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { ToggleType } from "models/ToggleType";
import { useApi } from "providers";
import { useCustomParams } from "providers";
import { useToasts } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { BoardContext } from "./BoardContext";
import { ConfirmDeleteBoard } from "./components/ConfirmDeleteBoard";

const logger = createLogger("BoardProvider");

const isExpandedDefault = false;

export const BoardProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { boardApi } = useApi();
  const { boardId } = useCustomParams();
  const i18n = useTranslations();
  const toasts = useToasts();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [expanded, setExpanded] = useState<ToggleType>({});
  const [confirmDeleteBoard, setConfirmDeleteBoard] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [deleting, setDeleting] = useState<ToggleType>({});

  const setBoardExpanded = useCallback(
    (id: number, newState: boolean) => {
      logger.debug(`setBoardExpanded(${id}): `, newState);
      setExpanded({ ...expanded, [id]: newState });
    },
    [expanded]
  );

  const isBoardExpanded = useCallback(
    (id: number, defaultValue?: boolean): boolean => {
      const isExpanded = expanded[id];
      if (isExpanded !== undefined) return isExpanded;
      if (defaultValue !== undefined) return defaultValue;
      return isExpandedDefault;
    },
    [expanded]
  );

  const toggleBoardExpanded = useCallback(
    (id: number) => {
      const before = expanded[id];
      const after = before == undefined ? !isExpandedDefault : !before;
      logger.debug(`toggleBoardExpanded(${id}): `, before, after);
      setBoardExpanded(id, after);
    },
    [setBoardExpanded, expanded]
  );

  const isBoardDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  const setBoardDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const queryResult = useQuery({
    queryKey: [`Board|${boardId}`],
    queryFn: async ({ signal }) => {
      const data = await boardApi.getBoard(boardId, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardId,
    staleTime: 0,
  });
  const { refetch, error } = queryResult;
  const isLoading = queryResult.isFetching;
  const board = queryResult.data;

  const reloadBoard = useCallback(() => {
    refetch().then((result) => {
      const reloadedData = result.data;
      if (!reloadedData) return;
      toasts.refreshSuccess(i18n.Board, reloadedData.name);
    });
  }, [refetch, toasts, i18n.Board]);

  useEffect(() => {
    // navigate to /board/:id when board is loaded and url doesn't match the board canonical name.
    // this also handles bookmarks  or shared links, ensuring the url matches the up-to-date value.
    const routeBoardId = getBoardRouteKey(boardId);
    const boardRouteId = getBoardRouteKey(board);
    if (!routeBoardId || !boardRouteId) return;
    if (routeBoardId == boardRouteId) return;

    navigate(pathname.replace(routeBoardId, boardRouteId), { replace: true });
  }, [board, boardId, navigate, pathname]);

  const boardPermissions = useMemo(() => {
    return (board?.boardPermissions || {}) as BoardPermissionModel;
  }, [board]);

  const contextValue = useMemo(() => {
    return {
      ...boardPermissions,
      boardPermissions,
      board: isLoading ? undefined : board,
      error: error as AxiosError | undefined,
      isLoading,
      reloadBoard,
      confirmDeleteBoard,
      setConfirmDeleteBoard,
      isBoardDeleting,
      setBoardDeleting,
      isBoardExpanded,
      toggleBoardExpanded,
      setBoardExpanded,
    };
  }, [
    board,
    boardPermissions,
    isLoading,
    error,
    reloadBoard,
    confirmDeleteBoard,
    setConfirmDeleteBoard,
    isBoardDeleting,
    setBoardDeleting,
    isBoardExpanded,
    toggleBoardExpanded,
    setBoardExpanded,
  ]);

  return (
    <BoardContext.Provider value={contextValue}>
      {children}
      <ConfirmDeleteBoard />
    </BoardContext.Provider>
  );
};
